import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Download, FileText } from "lucide-react";
import Cookies from "js-cookie";
import axios from "axios";
import ScrollbarWrapper from "../../ACS-Blocks/ScrollbarWrapper";
import Loader from "../../ACS-Blocks/Loader";

const getAuthToken = () =>
  localStorage.getItem("authToken") || Cookies.get("authToken");

const HelpDropdown = ({ onClose }) => {
  const dropdownRef = useRef(null);
  const [guides, setGuides] = useState([]);
  const [loading, setLoading] = useState(true);
  const [downloadingId, setDownloadingId] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchGuides = async () => {
      try {
        const response = await axios.get(
          `${process.env.REACT_APP_API_BASE_URL}/api/help-guides`,
          {
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              "Office-Code": Cookies.get("officeCode"),
            },
          }
        );
        const payload = response.data;
        setGuides(Array.isArray(payload) ? payload : payload.data ?? payload.content ?? []);
      } catch (err) {
        setError("Failed to load guides.");
      } finally {
        setLoading(false);
      }
    };

    fetchGuides();
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [onClose]);

  const handleDownload = async (guide) => {
    setDownloadingId(guide.id);
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/help-guides/${guide.id}/download`,
        {
          headers: {
            Authorization: `Bearer ${getAuthToken()}`,
            "Office-Code": Cookies.get("officeCode"),
          },
          responseType: "blob",
        }
      );
      const blobUrl = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = blobUrl;
      link.download = guide.fileName;
      link.click();
      URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error("Download failed:", err);
    } finally {
      setDownloadingId(null);
    }
  };

  const grouped = guides.reduce((acc, guide) => {
    const cat = guide.category || "General";
    if (!acc[cat]) acc[cat] = [];
    acc[cat].push(guide);
    return acc;
  }, {});

  return (
    <AnimatePresence>
      <motion.div
        ref={dropdownRef}
        initial={{ opacity: 0, scale: 0.95, y: -10 }}
        animate={{ opacity: 1, scale: 1, y: 0 }}
        exit={{ opacity: 0, scale: 0.95, y: -10 }}
        transition={{ duration: 0.2 }}
        className="absolute right-0 mt-fluid-2 z-[400] bg-white dark:bg-acs-dark-surface shadow-xl rounded-2xl w-[clamp(22rem,28vw,26rem)] border border-gray-200 dark:border-acs-dark-border overflow-hidden"
      >
        {/* Header */}
        <div className="px-fluid-2 py-fluid-1.5 border-b border-gray-200 dark:border-acs-dark-border">
          <h4 className="font-semibold text-fluid-base text-gray-800 dark:text-acs-dark-text">
            User Guides
          </h4>
          <p className="text-fluid-xs text-gray-500 dark:text-gray-400 mt-0.5">
            Download guides to get started
          </p>
        </div>

        {/* Content */}
        <ScrollbarWrapper className="max-h-[20rem] overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-6">
              <Loader compact message="Loading guides..." />
            </div>
          ) : error ? (
            <p className="text-fluid-xs text-red-500 text-center py-6">{error}</p>
          ) : guides.length === 0 ? (
            <p className="text-fluid-xs text-gray-400 text-center py-6">
              No guides available.
            </p>
          ) : (
            Object.entries(grouped).map(([category, items]) => (
              <div key={category}>
                <div className="px-fluid-2 py-1.5 bg-gray-50 dark:bg-acs-dark-hover">
                  <span className="text-fluid-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide">
                    {category}
                  </span>
                </div>
                {items.map((guide) => (
                  <div
                    key={guide.id}
                    className="flex items-center justify-between px-fluid-2 py-fluid-1.5 hover:bg-gray-50 dark:hover:bg-acs-dark-hover transition-colors"
                  >
                    <div className="flex items-center gap-fluid-1 min-w-0">
                      <FileText className="w-4 h-4 text-[#1e2a74] dark:text-yellow-400 flex-shrink-0" />
                      <div className="min-w-0">
                        <p className="text-fluid-sm font-medium text-gray-800 dark:text-acs-dark-text truncate">
                          {guide.title}
                        </p>
                        {guide.description && (
                          <p className="text-fluid-xs text-gray-400 truncate">
                            {guide.description}
                          </p>
                        )}
                        {guide.version && (
                          <span className="text-fluid-xs text-gray-400">
                            v{guide.version}
                          </span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => handleDownload(guide)}
                      disabled={downloadingId === guide.id}
                      className="ml-2 flex-shrink-0 p-1.5 rounded-md hover:bg-gray-200 dark:hover:bg-acs-dark-border transition text-gray-500 dark:text-gray-400 hover:text-[#1e2a74] dark:hover:text-yellow-400 disabled:opacity-50"
                      aria-label={`Download ${guide.title}`}
                    >
                      {downloadingId === guide.id ? (
                        <Loader compact message="" />
                      ) : (
                        <Download className="w-4 h-4" />
                      )}
                    </button>
                  </div>
                ))}
              </div>
            ))
          )}
        </ScrollbarWrapper>
      </motion.div>
    </AnimatePresence>
  );
};

export default HelpDropdown;
